/**
 * TokenStorage.js - Secure storage service for authentication tokens
 * 
 * This class provides a more secure alternative to localStorage for storing
 * authentication tokens, with support for different storage mechanisms
 * and proper error handling.
 */

import AuthError from './AuthError';

class TokenStorage {
  /**
   * Create a new TokenStorage instance
   * 
   * @param {Object} options - Configuration options
   * @param {string} options.namespace - Namespace prefix for storage keys
   * @param {Storage} options.storage - Storage mechanism (sessionStorage by default)
   */
  constructor(options = {}) {
    this.namespace = options.namespace || 'auth';
    // Use sessionStorage by default for better security (cleared when browser is closed)
    this.storage = options.storage || window.sessionStorage;
  }
  
  /**
   * Get token data from storage
   * 
   * @returns {Object|null} Token data object or null if not found
   */
  getToken() {
    try {
      const data = this.storage.getItem(`${this.namespace}_token`);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      const authError = new AuthError(
        'Error retrieving token from storage',
        'AUTH_STORAGE_ERROR',
        error,
      );
      authError.logError();
      return null;
    }
  }
  
  /**
   * Store token data
   * 
   * @param {Object} tokenData - Token data to store
   * @param {string} tokenData.value - The token string
   * @param {number} tokenData.expiresAt - Expiration timestamp in milliseconds
   */
  setToken(tokenData) {
    try {
      this.storage.setItem(
        `${this.namespace}_token`,
        JSON.stringify(tokenData),
      );
    } catch (error) {
      const authError = new AuthError(
        'Error storing token',
        'AUTH_STORAGE_ERROR',
        error,
      );
      authError.logError();
      
      // Attempt to clear storage if it might be full
      try {
        this.storage.removeItem(`${this.namespace}_token`);
      } catch (clearError) {
        // Ignore errors during cleanup
      }
    }
  }
  
  /**
   * Remove token from storage
   */
  removeToken() {
    try {
      this.storage.removeItem(`${this.namespace}_token`);
    } catch (error) {
      const authError = new AuthError(
        'Error removing token from storage',
        'AUTH_STORAGE_ERROR',
        error,
      );
      authError.logError();
    }
  }
  
  /**
   * Check if a token exists in storage
   * 
   * @returns {boolean} True if token exists, false otherwise
   */
  hasToken() {
    try {
      return this.storage.getItem(`${this.namespace}_token`) !== null;
    } catch (error) {
      return false;
    }
  }
}

export default TokenStorage;
